import { type ApiProblem, call } from '@rental/contracts/client'
import { format, plural } from '@rental/i18n'
import { Button, cn, Field, Notice } from '@rental/ui'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import { CircleAlert, TriangleAlert } from 'lucide-react'
import {
  type FormEvent,
  type ReactNode,
  type RefObject,
  useCallback,
  useEffect,
  useId,
  useRef,
  useState,
} from 'react'
import { flushSync } from 'react-dom'
import type { LockReason } from '../access'
import { IDLE_MINUTES, useAccess } from '../access-context'
import { api, load } from '../api'
import { KeptOnIpad } from '../capture/CaptureUi'
import { PinEntry } from '../components/PinEntry'
import { RomeClock } from '../components/RomeClock'
import { StatusChip } from '../components/StatusChip'
import { PageHeading, ProblemNotice, StaffInput } from '../components/ui'
import { useLocale } from '../i18n'
import { lockRow, ROW, readRecent } from '../lock-recents'
import { useOnline } from '../online'
import { problemOf, problemText, type StaffProblem } from '../problems'
import { Frame } from './Frame'

type DeviceUser = {
  id: string
  displayName: string
  role: string
  hasPin: boolean
  lockedHere: boolean
}

/**
 * The shared counter iPad: choose your name, type your PIN (UI-2a, the owner's
 * mock-up: the names above the PIN, on one card). Five wrong PINs lock that
 * person here; the password lets them back in.
 */
export function LockScreen({ reason }: { reason: LockReason }) {
  const { messages: m } = useLocale()
  const [mode, setMode] = useState<'pin' | 'password'>('pin')
  const device = useQuery({
    queryKey: ['device'],
    queryFn: () => load(call(api.GET('/v1/staff/device'))),
  })

  const queryClient = useQueryClient()
  // Back online: the people who can unlock are asked for again.
  const askAgain = useCallback(
    () => void queryClient.invalidateQueries({ queryKey: ['device'] }),
    [queryClient],
  )
  const online = useOnline(askAgain)
  const deviceProblem = problemOf(device.error)
  if (mode === 'password') return <PasswordSignIn onBack={() => setMode('pin')} />
  // S4: unlocking is the server's to grant; what was captured waits here meanwhile.
  const offline = !online || deviceProblem?.kind === 'network'
  const why =
    reason === 'idle'
      ? format(m.lock.autoLocked, { minutes: IDLE_MINUTES })
      : reason === 'expired'
        ? m.lock.expired
        : reason === 'handback'
          ? m.lock.handback
          : null
  return (
    <Frame
      device={device.data?.device.name}
      // Why the screen locked is news, not a task: beside the card, not in it, where the
      // keypad needs the height. The clock only where there is room beside the card.
      aside={
        <>
          <RomeClock className="hidden lg:block" />
          {why ? (
            <Notice tone="info">
              <p>{why}</p>
            </Notice>
          ) : null}
          <KeptOnIpad />
        </>
      }
    >
      {/* Once, above both: the heading takes the focus as the screen arrives, and keeps its
          place when the people's list comes. */}
      <PageHeading className="text-xl">{m.lock.title}</PageHeading>
      {device.data ? (
        <PinPad
          users={device.data.users}
          offline={offline}
          problem={deviceProblem?.kind === 'network' ? null : deviceProblem}
          retry={() => void device.refetch()}
          onPassword={() => setMode('password')}
        />
      ) : (
        <>
          {/* One line from the start, a status, as the names' own (UI-2g): the connection lost
              while they are read is heard, where a line that came with its words was not. */}
          <StatusLine key="line" tone={offline ? 'warning' : undefined} polite>
            {offline ? m.capture.offline : deviceProblem ? null : m.app.loading}
          </StatusLine>
          {deviceProblem ? (
            <>
              {/* Offline is said above; any other answer is said here, and can be asked again. */}
              {deviceProblem.kind === 'network' ? null : <ProblemNotice problem={deviceProblem} />}
              <Button
                variant="staff"
                block
                busy={device.isFetching}
                className="shadow-action"
                onClick={() => void device.refetch()}
              >
                {m.app.retry}
              </Button>
            </>
          ) : null}
          <Footer
            end={
              <QuietButton onClick={() => setMode('password')}>{m.lock.usePassword}</QuietButton>
            }
          />
        </>
      )}
    </Frame>
  )
}

/**
 * The names and the PIN on one card. Above the PIN: everyone when there are
 * three people or fewer, otherwise the last three to unlock this iPad
 * (`lock-recents.ts`), and "Altre persone" for everyone else. Nobody is
 * chosen for you: until someone is, the PIN waits, and says so.
 */
function PinPad({
  users,
  offline,
  problem,
  retry,
  onPassword,
}: {
  users: readonly DeviceUser[]
  offline: boolean
  /** The people read again and refused: said with a way to ask again (UI-2g). */
  problem: StaffProblem | null
  retry: () => void
  onPassword: () => void
}) {
  const { locale, messages: m } = useLocale()
  const { transition, takeOver } = useAccess()
  const queryClient = useQueryClient()
  const [recent] = useState(readRecent)
  const [chosenId, setChosenId] = useState<string | null>(null)
  const [view, setView] = useState<'row' | 'everyone'>(() =>
    users.length > ROW && lockRow(users, recent).length === 0 ? 'everyone' : 'row',
  )
  const [pin, setPin] = useState('')
  const [busy, setBusy] = useState(false)
  const [message, setMessage] = useState<string | null>(null)
  // Each thing said is a line of its own, so a screen reader hears each (UI-2g): the same
  // line given the alert's role and new words is often not read.
  const [said, setSaid] = useState(0)
  // Someone no longer on this iPad: said where the focus goes, read again from the server.
  const [gone, setGone] = useState<string | null>(null)
  const pinField = useRef<HTMLInputElement>(null)
  const tiles = useRef<HTMLUListElement>(null)
  const waitingId = useId()
  // The list read again may no longer hold whoever was chosen.
  const chosen = users.find((user) => user.id === chosenId) ?? null
  const row = lockRow(users, recent, chosen?.id ?? null)

  function say(words: string) {
    setMessage(words)
    setSaid((count) => count + 1)
  }

  /**
   * The row or everyone. The button pressed goes with the view it led from, so the
   * focus goes to the names, the chosen one first (UI-2g): left on nothing, it fell
   * back to the page's start.
   */
  function show(next: 'row' | 'everyone') {
    flushSync(() => setView(next))
    const names = tiles.current
    ;(
      names?.querySelector<HTMLElement>('[aria-pressed="true"]') ?? names?.querySelector('button')
    )?.focus()
  }

  function choose(user: DeviceUser) {
    setMessage(null)
    setGone(null)
    if (!user.hasPin || user.lockedHere) {
      onPassword()
      return
    }
    flushSync(() => {
      setChosenId(user.id)
      setPin('')
      setView('row')
    })
    pinField.current?.focus()
  }

  async function unlock(person: DeviceUser, value: string) {
    setBusy(true)
    setMessage(null)
    const result = await call(
      api.POST('/v1/staff/unlock', { body: { userId: person.id, pin: value } }),
    )
    setBusy(false)
    setPin('')
    if (result.ok) {
      transition({ state: 'unlocked', session: result.data })
      return
    }
    if (takeOver(result.problem)) return
    const code = result.problem.kind === 'api' ? result.problem.code : null
    if (code === 'user_not_found') {
      // No longer on this iPad: the names again, as the server now has them, and why, said
      // once where the focus goes.
      const words = describe(result.problem)
      await queryClient.invalidateQueries({ queryKey: ['device'] })
      setChosenId(null)
      setGone(words)
      return
    }
    say(describe(result.problem))
    if (code === 'pin_locked' || code === 'pin_not_set') {
      await queryClient.invalidateQueries({ queryKey: ['device'] })
    }
    pinField.current?.focus()
  }

  function describe(problem: ApiProblem): string {
    if (problem.kind === 'api' && problem.code === 'pin_invalid') {
      const left = problem.details.attemptsLeft
      if (typeof left === 'number') return plural(locale, m.lock.wrongPin, left)
    }
    return problemText(problem, locale, m)
  }

  /**
   * Sblocca, or Enter in the field. The sixth digit unlocks by itself, so a press
   * comes with fewer: it says what is missing and gives the focus back to the PIN
   * (UI-2g), where it did nothing and said nothing.
   */
  function submit(event: FormEvent) {
    event.preventDefault()
    if (!chosen || busy) return
    if (pin.length < 6) {
      say(m.lock.needSix)
      pinField.current?.focus()
      return
    }
    void unlock(chosen, pin)
  }

  /** The field and the keypad both type here; the sixth digit unlocks. */
  function enter(value: string) {
    if (!chosen) return
    const digits = value.replace(/\D/g, '').slice(0, 6)
    setPin(digits)
    if (digits.length === 6 && !busy) void unlock(chosen, digits)
  }

  // One line under the title, the subtitle's: what just happened takes its place
  // while it holds (a wrong PIN, someone removed, no network). A notice would grow
  // the card past what Safari leaves of an iPad on its side. Each alert is a line
  // of its own (UI-2g); the plain line is a status from the start, so the
  // connection lost there is heard, and the words it opens with are not.
  const status = gone ? (
    <StatusLine key={`gone:${gone}`} tone="danger" focus>
      {gone}
    </StatusLine>
  ) : message ? (
    <StatusLine key={`said:${said}`} tone="danger" alert>
      {message}
    </StatusLine>
  ) : problem ? (
    <StatusLine
      key="problem"
      tone="danger"
      alert
      action={<QuietButton onClick={retry}>{m.app.retry}</QuietButton>}
    >
      {problemText(problem, locale, m)}
    </StatusLine>
  ) : (
    <StatusLine key="line" tone={offline ? 'warning' : undefined} polite>
      {offline ? m.capture.offline : m.lock.pickUser}
    </StatusLine>
  )
  const toPassword = <QuietButton onClick={onPassword}>{m.lock.usePassword}</QuietButton>

  if (view === 'everyone') {
    return (
      <>
        {status}
        <Tiles listRef={tiles} people={users} chosenId={chosen?.id ?? null} onChoose={choose} />
        <Footer
          start={
            row.length > 0 ? (
              <QuietButton onClick={() => show('row')}>{m.lock.back}</QuietButton>
            ) : null
          }
          end={toPassword}
        />
      </>
    )
  }

  return (
    <>
      {status}
      <Tiles listRef={tiles} people={row} chosenId={chosen?.id ?? null} onChoose={choose} />
      <form onSubmit={submit} className="flex flex-col gap-3" noValidate>
        <h2 id={waitingId} className="text-center text-sm font-bold text-brand-strong">
          {chosen ? format(m.lock.pinFor, { name: chosen.displayName }) : m.lock.chooseFirst}
        </h2>
        <PinEntry
          label={m.lock.pinLabel}
          value={pin}
          onChange={enter}
          disabled={busy}
          waiting={!chosen}
          describedBy={chosen ? undefined : waitingId}
          inputRef={pinField}
        />
        <Button
          type="submit"
          variant="staff"
          block
          busy={busy}
          disabled={!chosen}
          aria-describedby={chosen ? undefined : waitingId}
          className="shadow-action"
        >
          {m.lock.unlock}
        </Button>
      </form>
      <Footer
        start={
          users.length > ROW ? (
            <QuietButton onClick={() => show('everyone')}>{m.lock.everyone}</QuietButton>
          ) : null
        }
        end={toPassword}
      />
    </>
  )
}

/**
 * The people as tiles, as in the owner's mock-up: initials, name, role. A
 * person locked out here or with no PIN yet wears that instead of the role,
 * and goes to the password. Known by their words, the tiles keep the faint
 * edge of the rule; the chosen one has the blue edge and its halo.
 */
function Tiles({
  listRef,
  people,
  chosenId,
  onChoose,
}: {
  listRef: RefObject<HTMLUListElement | null>
  people: readonly DeviceUser[]
  chosenId: string | null
  onChoose: (user: DeviceUser) => void
}) {
  const { messages: m } = useLocale()
  const roles = m.roles as Readonly<Record<string, string>>
  if (people.length === 0) return null
  return (
    // Three to a row, in six columns: a row of one or two stands in the middle.
    <ul ref={listRef} className="grid grid-cols-6 gap-2">
      {people.map((user, index) => {
        const chosen = user.id === chosenId
        return (
          <li
            key={user.id}
            className={cn(
              'col-span-2 flex',
              index === 0 && people.length === 2 && 'col-start-2',
              index === 0 && people.length === 1 && 'col-start-3',
            )}
          >
            <button
              type="button"
              aria-pressed={chosen}
              onClick={() => onChoose(user)}
              // `lock-tile`: in forced colours the chosen one keeps an edge of its own (styles.css).
              className={cn(
                'lock-tile flex w-full flex-col items-center gap-1 rounded-card border border-rule bg-surface-pane-strong px-1 pt-3 pb-2 text-center transition-colors hover:bg-surface-well',
                chosen && 'border-action-bg shadow-chosen ring-1 ring-action-bg',
              )}
            >
              <span
                aria-hidden="true"
                className="flex size-8 shrink-0 items-center justify-center rounded-control bg-mz-info-tint text-xs font-bold text-brand"
              >
                {initials(user.displayName)}
              </span>
              <span className="line-clamp-2 text-sm leading-heading font-bold text-brand-strong">
                {user.displayName}
              </span>
              {user.lockedHere ? (
                <StatusChip tone="danger" compact>
                  {m.lock.lockedHere}
                </StatusChip>
              ) : !user.hasPin ? (
                <StatusChip tone="info" compact>
                  {m.lock.noPin}
                </StatusChip>
              ) : (
                <span className="text-xs leading-heading text-text-muted">
                  {roles[user.role] ?? user.role}
                </span>
              )}
            </button>
          </li>
        )
      })}
    </ul>
  )
}

/**
 * The line under the title. Plain, it says what to do; with a tone, what just
 * happened: its icon, its words and its colour. `alert` announces it; `polite`
 * makes it a status from the start, so what it comes to say is heard and what
 * it starts with is not; `focus` takes the focus instead, when the control that
 * was pressed has gone. `action` ends the line, outside what is announced.
 */
function StatusLine({
  tone,
  alert = false,
  polite = false,
  focus = false,
  action,
  children,
}: {
  tone?: 'danger' | 'warning'
  alert?: boolean
  polite?: boolean
  focus?: boolean
  action?: ReactNode
  children: ReactNode
}) {
  const line = useRef<HTMLParagraphElement>(null)
  useEffect(() => {
    if (focus) line.current?.focus()
  }, [focus])
  const Icon = tone === 'danger' ? CircleAlert : tone === 'warning' ? TriangleAlert : null
  const words = (
    <p
      ref={line}
      tabIndex={focus ? -1 : undefined}
      role={alert ? 'alert' : polite ? 'status' : undefined}
      // Right under the title, closer than the card's other parts.
      className={cn(
        'flex items-start gap-2 text-sm focus:outline-none',
        !action && '-mt-2',
        tone === 'danger' ? 'text-danger' : tone === 'warning' ? 'text-warning' : 'text-text-muted',
      )}
    >
      {Icon ? <Icon aria-hidden="true" className="mt-1 size-4 shrink-0" /> : null}
      <span>{children}</span>
    </p>
  )
  return action ? (
    <div className="-mt-2 flex items-start justify-between gap-x-4">
      {words}
      {action}
    </div>
  ) : (
    words
  )
}

/** The card's last row: a way elsewhere at either end. */
function Footer({ start = null, end }: { start?: ReactNode; end: ReactNode }) {
  return (
    <div className="flex items-center justify-between gap-x-4">
      {start ?? <span />}
      {end}
    </div>
  )
}

/**
 * The footer's buttons: words that stand alone in the card's last row, not
 * links in a sentence, so no underline (UI-2a, the owner's mock-up; WCAG 1.4.1
 * is about links set in text). Their box grows to 44 px under a finger.
 */
function QuietButton({ onClick, children }: { onClick: () => void; children: ReactNode }) {
  return (
    <Button variant="link" onClick={onClick} className="touch-target min-h-0 text-sm no-underline">
      {children}
    </Button>
  )
}

/** The person's initials, for their tile: two at most, from the first two words of the name. */
function initials(name: string): string {
  return name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toLocaleUpperCase())
    .join('')
}

/** For someone without a PIN yet, or locked out of it here: the password, on this enrolled device. */
function PasswordSignIn({ onBack }: { onBack: () => void }) {
  const { messages: m } = useLocale()
  const { transition, takeOver } = useAccess()
  const navigate = useNavigate()
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [totp, setTotp] = useState('')
  const [askTotp, setAskTotp] = useState(false)
  const [busy, setBusy] = useState(false)
  const [problem, setProblem] = useState<ApiProblem | null>(null)
  const totpField = useRef<HTMLInputElement>(null)

  async function submit(event: FormEvent) {
    event.preventDefault()
    setBusy(true)
    setProblem(null)
    const result = await call(
      api.POST('/v1/staff/sign-in', {
        body: { email: email.trim(), password, ...(askTotp ? { totp: totp.trim() } : {}) },
      }),
    )
    setBusy(false)
    if (result.ok) {
      transition({ state: 'unlocked', session: result.data })
      if (result.data.pinMissing) void navigate({ to: '/pin' })
      return
    }
    if (takeOver(result.problem)) return
    setProblem(result.problem)
    // The code is asked for, or was wrong: the focus goes to its field.
    const code = result.problem.kind === 'api' ? result.problem.code : null
    if (code === 'totp_required' || code === 'totp_invalid') {
      flushSync(() => {
        setAskTotp(true)
        // A wrong code is typed again from the start, as at enrolment.
        if (code === 'totp_invalid') setTotp('')
      })
      totpField.current?.focus()
    }
  }

  return (
    <Frame>
      <div className="flex flex-col gap-1">
        <PageHeading className="text-xl">{m.signIn.title}</PageHeading>
        <p className="text-sm text-text-muted">{m.signIn.intro}</p>
      </div>
      <form onSubmit={submit} className="flex flex-col gap-4" noValidate>
        <Field label={m.enroll.email} required>
          {(control) => (
            <StaffInput
              {...control}
              type="email"
              autoComplete="username"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
            />
          )}
        </Field>
        <Field label={m.enroll.password} required>
          {(control) => (
            <StaffInput
              {...control}
              type="password"
              autoComplete="current-password"
              value={password}
              onChange={(event) => setPassword(event.target.value)}
            />
          )}
        </Field>
        {askTotp ? (
          <Field label={m.enroll.totp} hint={m.enroll.totpHint} required>
            {(control) => (
              <StaffInput
                {...control}
                ref={totpField}
                inputMode="numeric"
                autoComplete="one-time-code"
                maxLength={6}
                value={totp}
                onChange={(event) => setTotp(event.target.value.replace(/\D/g, ''))}
              />
            )}
          </Field>
        ) : null}
        <ProblemNotice problem={problem} />
        <Button type="submit" variant="staff" block busy={busy} className="shadow-action">
          {m.signIn.submit}
        </Button>
      </form>
      <Footer
        start={<QuietButton onClick={onBack}>{m.signIn.backToLock}</QuietButton>}
        end={null}
      />
    </Frame>
  )
}
